import { Commit } from 'vuex';
import { Nullable, GenericDict } from 'frontend/types';
import { postData } from 'frontend/utils/ajax';
import { ErrorsDict } from 'frontend/types/lessons/requests';

type SuccessFlag = boolean;
type ApiRequestResult = [SuccessFlag, Nullable<GenericDict>];
type ApiRequestResultWithErrors = [SuccessFlag, Nullable<GenericDict>, string[]];

function setErrors(commit: Commit, errors: ErrorsDict) {
  commit('setErrors', errors);
}

export function handleReturnedErrors(commit: Commit, response: any): boolean {
  if (response?.results?.elm_compile_errors?.errors) {
    setErrors(commit, {
      requestErrors: ['Elm could not compile'],
      elmerErrors: response.results.elm_compile_errors.errors,
    });
    return true;
  }
  if (response?.results?.errors) {
    setErrors(commit, { requestErrors: response.results.errors, elmerErrors: null });
    return true;
  }
  return false;
}

export function handleRequestErrors(commit: Commit, response: any) {
  switch (response?.status) {
    case 400:
      setErrors(commit, {
        requestErrors: response?.data?.results?.errors || ['Bad request (unspecified)'],
        elmerErrors: null,
      });
      return [false, null];
    case 500:
      setErrors(commit, {
        requestErrors: response?.data?.results?.errors || ['Server error (unspecified)'],
        elmerErrors: null,
      });
      return [false, null];
    default:
      setErrors(commit, {
        requestErrors: [`Unhandled error status: ${response?.status || '???'}`],
        elmerErrors: null,
      });
      return [false, null];
  }
}

export async function apiRequest(
  commit: Commit,
  url: string,
  formData: object,
): Promise<ApiRequestResult> {
  try {
    const response = await postData(url, formData);
    const hasErrors = handleReturnedErrors(commit, response);
    if (hasErrors) {
      return [false, null];
    }
    setErrors(commit, { requestErrors: [], elmerErrors: null });
    return [true, response.results];
  } catch (e) {
    const hasErrors = handleReturnedErrors(commit, e?.response?.data);
    if (hasErrors) {
      return [false, null];
    }
    handleRequestErrors(commit, e?.response);
    return [false, null];
  }
}

export async function apiRequestWithErrors(
  url: string,
  formData: object,
): Promise<ApiRequestResultWithErrors> {
  try {
    const response = await postData(url, formData);
    if (response?.results?.errors?.length) {
      return [false, null, response.results.errors];
    }
    return [true, response.results, []];
  } catch (e) {
    const errors = e.response?.data?.results?.errors?.length
      ? e.response.data.results.errors
      : [`Unspecified error (${e?.response?.status || 'unknown status'})`];
    return [false, e.response?.data?.results, errors];
  }
}
